import { useState } from 'react';
import {
  Loader2,
  X,
  UserPlus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Clock,
  Database,
  Wifi,
} from 'lucide-react';
import {
  getSitePermissions,
  manageSitePermission,
  SitePermissionGroup,
  SiteGroupMember,
} from '../../services/api';
import { SyncedSitePermission } from '../../types';

interface SitePermissionsPanelProps {
  clientId: string;
  siteUrl: string;
  siteName: string;
  onClose: () => void;
  /** Cached permissions from sync data (if available) */
  cachedPermissions?: SyncedSitePermission | null;
  /** When the cache was last synced */
  cachedAt?: string | null;
}

type DataSource = 'cache' | 'live';

export default function SitePermissionsPanel({
  clientId,
  siteUrl,
  siteName,
  onClose,
  cachedPermissions,
  cachedAt,
}: SitePermissionsPanelProps) {
  const hasCached = !!cachedPermissions && cachedPermissions.groups.length > 0;

  const [groups, setGroups] = useState<SitePermissionGroup[]>(() => {
    if (hasCached) {
      // Convert cached data to SitePermissionGroup format
      return cachedPermissions!.groups.map(g => ({
        name: g.name,
        roleType: g.role,
        memberCount: g.members.length,
        members: g.members.map(m => ({
          displayName: m,
          email: '',
          loginName: m,
        })),
      }));
    }
    return [];
  });
  const [loading, setLoading] = useState(!hasCached);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    if (hasCached) {
      return new Set(cachedPermissions!.groups.map(g => g.name));
    }
    return new Set();
  });
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<DataSource>(hasCached ? 'cache' : 'live');
  const [liveLoading, setLiveLoading] = useState(false);

  const fetchPermissions = async () => {
    setLiveLoading(true);
    setLoading(true);
    setError(null);
    try {
      const result = await getSitePermissions(clientId, siteUrl);
      if (result.success) {
        setGroups(result.groups);
        setExpandedGroups(new Set(result.groups.map(g => g.name)));
        setDataSource('live');
      } else {
        setError(result.error || 'Failed to fetch permissions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
    } finally {
      setLoading(false);
      setLiveLoading(false);
    }
  };

  // Fetch on mount only if no cached data
  useState(() => {
    if (!hasCached) {
      fetchPermissions();
    }
  });

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const handleRemoveMember = async (groupName: string, member: SiteGroupMember) => {
    const key = `${groupName}:${member.loginName}`;
    if (!confirm(`Remove ${member.displayName || member.email} from ${groupName}?`)) return;

    setActionLoading(key);
    setActionError(null);
    try {
      const result = await manageSitePermission(clientId, {
        siteUrl,
        userEmail: member.email || member.loginName,
        groupName,
        action: 'remove',
      });
      if (result.success) {
        setGroups(prev =>
          prev.map(g =>
            g.name === groupName
              ? { ...g, members: g.members.filter(m => m.loginName !== member.loginName), memberCount: g.memberCount - 1 }
              : g
          )
        );
      } else {
        setActionError(result.error || 'Failed to remove member');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddMember = async (groupName: string) => {
    if (!newEmail.trim()) return;

    const key = `add:${groupName}`;
    setActionLoading(key);
    setActionError(null);
    try {
      const result = await manageSitePermission(clientId, {
        siteUrl,
        userEmail: newEmail.trim(),
        groupName,
        action: 'add',
      });
      if (result.success) {
        setNewEmail('');
        setAddingTo(null);
        // Re-fetch live to get updated member list
        await fetchPermissions();
      } else {
        setActionError(result.error || 'Failed to add member');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleColor = (roleType: string) => {
    const lower = roleType.toLowerCase();
    if (lower.includes('owner') || lower.includes('full control')) return 'text-red-600 dark:text-red-400';
    if (lower.includes('member') || lower.includes('edit') || lower.includes('contribute')) return 'text-blue-600 dark:text-blue-400';
    if (lower.includes('visitor') || lower.includes('read')) return 'text-green-600 dark:text-green-400';
    return 'text-muted-foreground';
  };

  // Calculate staleness for the badge
  const getStalenessInfo = (): { label: string; color: string } | null => {
    if (dataSource !== 'cache' || !cachedAt) return null;
    const hours = (Date.now() - new Date(cachedAt).getTime()) / (1000 * 60 * 60);
    if (hours < 4) return { label: `${Math.round(hours * 60)}m ago`, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
    if (hours < 24) return { label: `${Math.round(hours)}h ago`, color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
    return { label: `${Math.round(hours / 24)}d ago`, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
  };

  const staleness = getStalenessInfo();

  return (
    <div className="border border-border rounded-lg bg-card mt-3">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-foreground truncate">{siteName} — Permissions</h4>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-muted-foreground truncate">{siteUrl}</p>
            {/* Data source indicator */}
            {dataSource === 'cache' && staleness && (
              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${staleness.color}`}>
                <Database className="h-2.5 w-2.5" />
                Cached {staleness.label}
              </span>
            )}
            {dataSource === 'live' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <Wifi className="h-2.5 w-2.5" />
                Live
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {/* Load Live button — shown when viewing cached data */}
          {dataSource === 'cache' && !liveLoading && (
            <button
              onClick={fetchPermissions}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 rounded transition-colors"
              title="Fetch fresh data from SharePoint (30-60s)"
            >
              <Wifi className="h-3 w-3" />
              Load Live
            </button>
          )}
          <button
            onClick={fetchPermissions}
            disabled={loading || liveLoading}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors"
            title="Refresh permissions (live runbook)"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || liveLoading) ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading && groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Loading site permissions...</p>
            <p className="text-xs text-muted-foreground mt-1">This uses a PowerShell runbook and may take 30-60 seconds</p>
          </div>
        ) : error && groups.length === 0 ? (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load permissions</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              <button
                onClick={fetchPermissions}
                className="text-sm text-red-600 dark:text-red-400 underline mt-2"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No permission groups found for this site.</p>
        ) : (
          <div className="space-y-3">
            {/* Live loading overlay banner */}
            {liveLoading && groups.length > 0 && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                Refreshing from SharePoint... showing cached data in the meantime.
              </div>
            )}

            {actionError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {actionError}
                <button onClick={() => setActionError(null)} className="ml-auto">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {groups.map(group => (
              <div key={group.name} className="border border-border rounded-lg">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.name)}
                  className="w-full flex items-center justify-between p-3 hover:bg-accent/50 rounded-t-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedGroups.has(group.name) ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm text-foreground">{group.name}</span>
                    <span className={`text-xs font-medium ${getRoleColor(group.roleType)}`}>
                      {group.roleType}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                  </span>
                </button>

                {/* Members */}
                {expandedGroups.has(group.name) && (
                  <div className="border-t border-border">
                    {group.members.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-3">No members</p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {group.members.map(member => (
                          <li key={member.loginName} className="flex items-center justify-between px-3 py-2 text-sm">
                            <div className="min-w-0 flex-1">
                              <span className="text-foreground">{member.displayName || member.email || member.loginName}</span>
                              {member.email && member.email !== member.displayName && (
                                <span className="text-muted-foreground ml-2 text-xs">{member.email}</span>
                              )}
                            </div>
                            {/* Only show remove button when we have live data (cached members lack loginName detail) */}
                            {dataSource === 'live' && (
                              <button
                                onClick={() => handleRemoveMember(group.name, member)}
                                disabled={actionLoading === `${group.name}:${member.loginName}`}
                                className="p-1 text-muted-foreground hover:text-red-500 rounded transition-colors flex-shrink-0"
                                title={`Remove ${member.displayName || member.email} from ${group.name}`}
                              >
                                {actionLoading === `${group.name}:${member.loginName}` ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <X className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Add Member — only when live data is loaded */}
                    {dataSource === 'live' && (
                      <div className="p-3 border-t border-border">
                        {addingTo === group.name ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="email"
                              value={newEmail}
                              onChange={e => setNewEmail(e.target.value)}
                              placeholder="user@domain.com"
                              className="flex-1 text-sm border border-border rounded px-2 py-1.5 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleAddMember(group.name);
                                if (e.key === 'Escape') { setAddingTo(null); setNewEmail(''); }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => handleAddMember(group.name)}
                              disabled={!newEmail.trim() || actionLoading === `add:${group.name}`}
                              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 transition-colors"
                            >
                              {actionLoading === `add:${group.name}` ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                'Add'
                              )}
                            </button>
                            <button
                              onClick={() => { setAddingTo(null); setNewEmail(''); }}
                              className="px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddingTo(group.name); setNewEmail(''); }}
                            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                            Add Member
                          </button>
                        )}
                      </div>
                    )}

                    {/* Hint to load live for management actions when viewing cached */}
                    {dataSource === 'cache' && (
                      <div className="px-3 py-2 border-t border-border">
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Load live data to add/remove members
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
